import React, { useEffect, useCallback, useMemo, useRef } from "react"

import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    FormFeedback,
    Label,
    Row,
} from "reactstrap"

import { useFormik } from "formik"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import { convertLocalToUTC } from "helpers/date_helper"
import { uploadBase64File } from "helpers/backend_helper"
import { API_ENCRYPTION_MODES } from "helpers/api_constants"
import Dropzone from "react-dropzone"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import TextInput from "components/Common/TextInput"
import TextArea from "components/Common/TextArea"
import SelectInput from "components/Common/SelectInput"
import CButton from "components/Common/CButton"
import CheckboxInput from "components/Common/CheckboxInput"
import IconButton from "components/Common/IconButton"
import EntitySelectionModal from "components/Common/EntitySelectionModal"
import PopupModal from "components/Common/PopupModel"
import * as url from "../../helpers/url_helper"
import MerchantDetail from "../Merchants/merchant-detail"

import {
    getCuratorOffers as onGetCuratorOffers,
    getOperationOffers as onGetOperationOffers,
    getApproverOffers as onGetApproverOffers,
    getPublishedOffers as onGetPublishedOffers,
    manageCuratorOffer,
    manageOperationOffer,
    managePublishedOffer
} from "store/offers/actions"
import { getBrands as onGetBrands, getBrandDetail as onGetBrandDetail } from "store/actions"
import { getMerchants as onGetMerchants, getMerchantDetail as onGetMerchantDetail } from "store/merchants/actions"

const getOfferFromState = (offersState, offerType, offerID) => {
    const offers = {
        curator: offersState.curatorOffers,
        operation: offersState.operationOffers,
        approver: offersState.approverOffers,
        published: offersState.publishedOffers
    }

    return offers[offerType]?.find(offer => offer.offerID === offerID) || null
}

const getContextUri = (offerType) => {
    switch (offerType) {
        case 'curator': return url.MANAGE_CURATOR_OFFER
        case 'operation': return url.MANAGE_OPERATIONS_OFFER
        case 'published': return url.MANAGE_PUBLISHED_OFFER
        default: return ''
    }
}

// Get editable fields for published offers
const getPublishedEditableFields = () => [
    'description',
    'validityWeekdays',
    'validityVoph',
    'userCashbackPercentage',
    'imageFile',
    'activeFrom',
    'activeUntil'
]

// Check if field is editable for published offers
const isFieldEditableForPublished = (fieldName) => {
    return getPublishedEditableFields().includes(fieldName)
}

// Validation schema
const getValidationSchema = (offerType) => {
    const baseSchema = {
        brandID: Yup.string().required("Please Select Brand"),
        merchantID: Yup.string().required("Please Select Merchant"),
        label: Yup.string()
            .required("Please Enter Offer Label")
            .max(255, "Offer Label cannot be greater than 255 characters"),
        description: Yup.string()
            .required("Please Enter Offer Description")
            .max(510, "Offer Description cannot be greater than 510 characters"),
        currencyID: Yup.mixed().nullable().required("Please Select Currency"),
        validityWeekdays: Yup.array()
            .min(1, "Please select at least one valid day")
            .required("Please select valid week days"),
        validityVoph: Yup.boolean(),
        image: Yup.string(),
        imageFile: Yup.mixed()
            .nullable()
            .test(
                "imageFile",
                "Invalid file",
                async function (value) {
                    if (!value) {
                        return true
                    }

                    if (value.preview && !value.type && !value.size) {
                        return true
                    }

                    try {
                        await validateImageFile(value, "offer", 1100, 550, 1)
                        return true
                    } catch (error) {
                        return this.createError({ message: error.message })
                    }
                }
            ),
        activeFrom: Yup.date(),
        activeUntil: Yup.date(),
    }

    // Published offers have restricted validation - only validate editable fields
    if (offerType === 'published') {
        return Yup.object({
            description: Yup.string()
                .required("Please Enter Offer Description")
                .max(510, "Offer Description cannot be greater than 510 characters"),
            validityWeekdays: Yup.array()
                .min(1, "Please select at least one valid day")
                .required("Please select valid week days"),
            validityVoph: Yup.boolean(),
            userCashbackPercentage: Yup.number()
                .required("Please Enter User Cashback Percentage")
                .min(0, "Cashback percentage must be positive")
                .max(100, "Cashback percentage cannot exceed 100%"),
            imageFile: baseSchema.imageFile,
            activeFrom: Yup.date().required("Please Enter Active From Date"),
            activeUntil: Yup.date(),
        })
    }

    if (offerType === 'operation') {
        return Yup.object({
            ...baseSchema,
            userCashbackPercentage: Yup.number()
                .required("Please Enter User Cashback Percentage")
                .min(0, "Cashback percentage must be positive")
                .max(100, "Cashback percentage cannot exceed 100%"),

            activeFrom: Yup.date().required("Please Enter Active From Date"),
            activeUntil: Yup.date(),
        })
    } else {
        return Yup.object({
            ...baseSchema,
            merchantSettlementPercentage: Yup.number()
                .required("Please Enter Merchant Settlement Percentage")
                .min(0, "Settlement percentage must be positive"),
            maxCashbackAmount: Yup.number()
                .min(0, "Maximum cashback amount must be positive"),
            activeFrom: Yup.date(),
            activeUntil: Yup.date(),
        })
    }
}

export default function OfferForm({
    page,
    match,
    history,
    offerType,
    canEdit,
    onOfferCreated,
    onTabCompletion,
    onFormChange,
}) {
    // Parse query parameters for copy functionality
    const urlParams = new URLSearchParams(history.location.search)
    const copyFromID = urlParams.get('copyFrom')
    const fromType = urlParams.get('fromType')

    const [readOnly, setReadOnly] = React.useState(page === "add" ? false : true)
    const [hasFormChanged, setHasFormChanged] = React.useState(false)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [createdOfferID, setCreatedOfferID] = React.useState(null)

    const [showMerchantModal, setShowMerchantModal] = React.useState(false)
    const [selectedMerchant, setSelectedMerchant] = React.useState(null)

    const [showBrandModal, setShowBrandModal] = React.useState(false)
    const [selectedBrand, setSelectedBrand] = React.useState(null)

    // Merchant detail modal state
    const [showMerchantDetailModal, setShowMerchantDetailModal] = React.useState(false)

    const [selectedFiles, setSelectedFiles] = React.useState([])
    const [imageCleared, setImageCleared] = React.useState(false)
    const [isUploading, setIsUploading] = React.useState(false)
    const [uploadError, setUploadError] = React.useState("")

    const initialDataSet = useRef(false)
    const prevManagementSuccess = useRef(null)
    const prevCreationSuccess = useRef(null)
    const prevOfferError = useRef(null)

    const dispatch = useDispatch()
    const offerID = match?.params?.id && match?.params?.id !== "add" ? match.params.id : null

    const {
        offers,
        offerError,
        merchants,
        brands,
        currencies,
    } = useSelector(state => ({
        offers: state.offers,
        offerError: state.offers[`${offerType}DeleteError`] || null,
        merchants: state.merchants?.merchants || [],
        brands: state.brands?.brands || [],
        currencies: state.appInfo?.appInfo?.currencies || [],
    }))


    const offer = getOfferFromState(offers, offerType, offerID)

    // For copy functionality, get the source offer
    const copyOffer = copyFromID && fromType ? getOfferFromState(offers, fromType, copyFromID) : null

    const isDataLoaded = useMemo(() => {
        if (page === "add") {
            // For copy functionality, wait for both currencies and copy offer data
            if (copyFromID && fromType) {
                return currencies.length > 0 && copyOffer && copyOffer.offerID
            }
            return currencies.length > 0
        }

        const hasOffer = offer && offer.offerID
        const hasCurrencies = currencies.length > 0

        return hasOffer && hasCurrencies
    }, [offer, copyOffer, currencies, page, copyFromID, fromType])

    const getInitialValues = useCallback((offer, offerType, currencies) => {
        const baseValues = {
            offerID: (copyFromID && page === "add") ? "" : offer?.offerID || "",
            merchantID: offer?.merchant?.merchantID || "",
            brandID: offer?.brand?.brandID || "",
            label: offer?.label || "",
            description: offer?.description || "",
            validityWeekdays: offer?.validityWeekdays || [],
            validityVoph: offer?.validityVoph || false,
            minInvoiceAmount: offer?.minInvoiceAmount || "",
            maxInvoiceAmount: offer?.maxInvoiceAmount || "",
            userCashbackPercentage: offer?.userCashbackPercentage || "",
            maxCashbackAmount: offer?.maxCashbackAmount || "",
            merchantSettlementPercentage: offer?.merchantSettlementPercentage || "",
            currencyID: offer?.currency?.currencyID && {
                value: offer.currency.currencyID,
                label: `${offer.currency.isoCode} - ${offer.currency.name}`
            },
            image: offer?.image || "",
            imageFile: offer?.image ? { preview: offer.image } : "",
            weekDay_Mon: offer?.validityWeekdays?.includes("Mon") || false,
            weekDay_Tue: offer?.validityWeekdays?.includes("Tue") || false,
            weekDay_Wed: offer?.validityWeekdays?.includes("Wed") || false,
            weekDay_Thu: offer?.validityWeekdays?.includes("Thu") || false,
            weekDay_Fri: offer?.validityWeekdays?.includes("Fri") || false,
            weekDay_Sat: offer?.validityWeekdays?.includes("Sat") || false,
            weekDay_Sun: offer?.validityWeekdays?.includes("Sun") || false,
            activeFrom: offer?.activeFrom || "",
            activeUntil: offer?.activeUntil || "",
        }

        return baseValues
    }, [copyFromID, page])


    // Handle copy functionality
    useEffect(() => {
        if (copyFromID && fromType && page === "add") {

            const loadAction = {
                curator: () => dispatch(onGetCuratorOffers({
                    headers: { "input-parameters": { offerID: copyFromID } },
                    // encDecAlgo: API_ENCRYPTION_MODES.BASE64,
                })),
                operation: () => dispatch(onGetOperationOffers({
                    headers: { "input-parameters": { offerID: copyFromID } },
                })),
                approver: () => dispatch(onGetApproverOffers({
                    headers: { "input-parameters": { offerID: copyFromID } },
                })),
                published: () => dispatch(onGetPublishedOffers({
                    headers: { "input-parameters": { offerID: copyFromID } },
                }))
            }

            if (loadAction[fromType]) {
                loadAction[fromType]()
            }
        }
    }, [copyFromID, fromType, page, dispatch])

    // Load offer data
    useEffect(() => {
        if (offerID && page !== "add") {
            const loadAction = {
                curator: () => dispatch(onGetCuratorOffers({
                    headers: { "input-parameters": { offerID: offerID.toString() } },
                    pagination: { startIndex: 0 },
                })),
                operation: () => dispatch(onGetOperationOffers({
                    headers: { "input-parameters": { offerID: offerID.toString() } },
                    pagination: { startIndex: 0 },
                })),
                approver: () => dispatch(onGetApproverOffers({
                    headers: { "input-parameters": { offerID: offerID.toString() } },
                    pagination: { startIndex: 0 },
                })),
                published: () => dispatch(onGetPublishedOffers({
                    headers: { "input-parameters": { offerID: offerID.toString() } },
                    pagination: { startIndex: 0 },
                }))
            }

            if (loadAction[offerType]) {
                loadAction[offerType]()
            }
        }
    }, [dispatch, offerID, offerType, page])

    // Currency options
    const currencyOptions = useMemo(() => {
        return currencies.map(currency => ({
            value: currency.currencyID,
            label: `${currency.isoCode} - ${currency.name}`,
        }))
    }, [currencies])

    // Formik validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: isDataLoaded ? getInitialValues(copyOffer || offer, offerType, currencies) : {
            offerID: "",
            merchantID: "",
            brandID: "",
            label: "",
            description: "",
            validityWeekdays: [],
            validityVoph: false,
            minInvoiceAmount: "",
            maxInvoiceAmount: "",
            currencyID: null,
            image: "",
            imageFile: "",
            weekDay_Mon: false,
            weekDay_Tue: false,
            weekDay_Wed: false,
            weekDay_Thu: false,
            weekDay_Fri: false,
            weekDay_Sat: false,
            weekDay_Sun: false,
            ...(offerType === 'operation' ? {
                userCashbackPercentage: "",
                maxCashbackAmount: "",
            } : {
                merchantSettlementPercentage: "",
            })
        },
        validationSchema: getValidationSchema(offerType),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Handle form submission
    const handleConfirmedSubmit = async () => {
        setIsSubmitting(true)
        const values = validation.values

        let imagePath = ""

        // Handle image upload logic
        if (values.imageFile && values.imageFile.base64encodedFileByteArray) {
            // User uploaded a new image
            try {
                const uploadResponse = await uploadBase64File(values.imageFile, {})
                if (uploadResponse?.fileName) {
                    imagePath = uploadResponse.fileName
                } else {
                    throw new Error("Upload failed - no filename returned")
                }
            } catch (error) {
                console.error("Image upload error:", error)
                setIsSubmitting(false)
                return
            }
        } else if (values.imageFile && values.imageFile.preview && !values.imageFile.base64encodedFileByteArray) {
            // User has existing image and didn't change it - extract filename from URL
            const url = values.imageFile.preview
            const urlParts = url.split('/')
            imagePath = urlParts[urlParts.length - 1] // Get the last part of the URL (filename)
        } else if (values.imageFile === "") {
            // User explicitly cleared the image
            imagePath = ""
        }

        const basePayload = {
            actionType: page === 'add' && !offerID && !createdOfferID ? 'create' : 'update',
            merchantID: values.merchantID,
            brandID: values.brandID,
            label: values.label,
            description: values.description,
            validityWeekdays: values.validityWeekdays,
            validityVoph: values.validityVoph,
            currencyID: values.currencyID?.value || "",
            image: imagePath,
            activeFrom: convertLocalToUTC(values.activeFrom),
            activeUntil: convertLocalToUTC(values.activeUntil),
        }

        let payload
        if (offerType === 'published') {
            // Published offers: only send allowed fields
            payload = {
                offerID: values.offerID || offerID || createdOfferID,
                description: values.description,
                validityWeekdays: values.validityWeekdays,
                validityVoph: values.validityVoph.toString(), // API expects string
                userCashbackPercentage: values.userCashbackPercentage.toString(), // API expects string
                image: imagePath,
                activeFrom: convertLocalToUTC(values.activeFrom),
                activeUntil: convertLocalToUTC(values.activeUntil),
            }
        } else if (offerType === 'operation') {
            payload = {
                ...basePayload,
                userCashbackPercentage: values.userCashbackPercentage,
            }
        } else {
            payload = {
                ...basePayload,

                maxCashbackAmount: values.maxCashbackAmount,
                merchantSettlementPercentage: values.merchantSettlementPercentage,
                minInvoiceAmount: values.minInvoiceAmount,
                maxInvoiceAmount: values.maxInvoiceAmount,
            }
        }

        if (basePayload.actionType === 'update') {
            payload.offerID = values.offerID || offerID || createdOfferID
        }

        const manageAction = {
            curator: manageCuratorOffer,
            operation: manageOperationOffer,
            published: managePublishedOffer
        }

        try {
            if (manageAction[offerType]) {
                await dispatch(manageAction[offerType](payload, history))

                if (basePayload.actionType === 'update' && offerID) {
                    setTimeout(() => {
                        const loadAction = {
                            curator: () => dispatch(onGetCuratorOffers({
                                headers: { "input-parameters": { offerID: offerID.toString() } },
                                pagination: { startIndex: 0 },
                            })),
                            operation: () => dispatch(onGetOperationOffers({
                                headers: { "input-parameters": { offerID: offerID.toString() } },
                                pagination: { startIndex: 0 },
                            })),
                            approver: () => dispatch(onGetApproverOffers({
                                headers: { "input-parameters": { offerID: offerID.toString() } },
                                pagination: { startIndex: 0 },
                            })),
                            published: () => dispatch(onGetPublishedOffers({
                                headers: { "input-parameters": { offerID: offerID.toString() } },
                                pagination: { startIndex: 0 },
                            }))
                        }

                        if (loadAction[offerType]) {
                            loadAction[offerType]()
                        }
                    }, 500)
                }
            }
        } catch (error) {
            console.error("Submit error:", error)
            setIsSubmitting(false)
        }
    }

    // Notify parent of form changes for breadcrumb secondary info
    useEffect(() => {
        if (onFormChange) {
            onFormChange({
                merchantLegalName: selectedMerchant?.legalEntityName || "",
                merchantLegalEntityAddress: selectedMerchant?.legalEntityAddress || "",
                userCashbackPercentage: validation.values.userCashbackPercentage || "",
                offerLabel: validation.values.label || ""
            })
        }
    }, [selectedMerchant?.legalEntityName, selectedMerchant?.legalEntityAddress, validation.values.userCashbackPercentage, validation.values.label])

    // Merchant handlers
    const handleMerchantModalOpen = useCallback(() => {
        setShowMerchantModal(true)
    }, [])

    const handleMerchantFetch = useCallback(({ search, page }) => {
        const startIndex = (page - 1) * 10
        dispatch(
            onGetMerchants({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }, [dispatch])

    const handleMerchantSelect = useCallback((merchant) => {
        setSelectedMerchant(merchant)
        setShowMerchantModal(false)
        validation.setFieldValue("merchantID", merchant.merchantID)
    }, [validation])

    const handleMerchantClear = useCallback(() => {
        setSelectedMerchant(null)
        validation.setFieldValue("merchantID", "")
    }, [validation])

    // Brand handlers
    const handleBrandModalOpen = useCallback(() => {
        setShowBrandModal(true)
    }, [])

    const handleBrandFetch = useCallback(({ search, page }) => {
        const startIndex = (page - 1) * 10
        dispatch(
            onGetBrands({
                headers: {
                    "input-parameters": { keyword: search || "" },
                },
                pagination: { startIndex },
            })
        )
    }, [dispatch])

    const handleBrandSelect = useCallback((brand) => {
        setSelectedBrand(brand)
        setShowBrandModal(false)
        validation.setFieldValue("brandID", brand.brandID)
    }, [validation])

    const handleBrandClear = useCallback(() => {
        setSelectedBrand(null)
        validation.setFieldValue("brandID", "")
    }, [validation])

    // Image handlers
    const handleFileSelection = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            handleImageUpload(acceptedFiles)
        }
    }, [])

    const handleImageUpload = useCallback(async (files) => {
        if (!files.length) return

        setIsUploading(true)
        setUploadError("")
        const file = files[0]

        try {
            await validateImageFile(file, "offer", 1100, 550, 1)

            const base64Img = await convertFileToBase64(file)

            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
                forExistingResource: false,
                base64encodedFileByteArray: base64Img,
                resourceType: "AMOS_OFFER_IMAGE",
            })

            setSelectedFiles([file])
            setImageCleared(false) // Reset cleared flag when uploading new image
            validation.setFieldValue("imageFile", file)
            setUploadError("")
        } catch (error) {
            console.error("Upload error:", error)
            setUploadError(error.message || "Upload failed")
        } finally {
            setIsUploading(false)
        }
    }, [validation])

    const clearImage = useCallback(() => {
        setSelectedFiles([])
        setImageCleared(true)
        setUploadError("")
        validation.setFieldValue("imageFile", "")
        validation.setFieldValue("image", "")
    }, [validation])

    useEffect(() => {
        const sourceOffer = copyOffer || offer
        if (sourceOffer && isDataLoaded) {
            // Only reset selected entities when in read-only mode or initially loading (not after user interactions)
            if (readOnly || !initialDataSet.current) {
                if (sourceOffer.merchant && (!selectedMerchant || selectedMerchant.merchantID !== sourceOffer.merchant.merchantID)) {
                    setSelectedMerchant(sourceOffer.merchant)
                }

                if (sourceOffer.brand && (!selectedBrand || selectedBrand.brandID !== sourceOffer.brand.brandID)) {
                    setSelectedBrand(sourceOffer.brand)
                }
            }

            // Set image for detail view or copy functionality (only on initial load)
            if (((page === "detail" && readOnly) || (page === "add" && copyOffer)) && sourceOffer.image && !imageCleared && !initialDataSet.current) {
                const file = { preview: sourceOffer.image }
                setSelectedFiles([file])
            }

            if (!initialDataSet.current) {
                initialDataSet.current = true
            }
        }
    }, [offer, copyOffer, isDataLoaded, page, readOnly, selectedMerchant, selectedBrand, imageCleared])

    // Clear data on mount for add mode, and reset when copyFromID or location changes
    useEffect(() => {
        if (page === "add") {
            setSelectedMerchant(null)
            setSelectedBrand(null)
            setSelectedFiles([])
            setImageCleared(false)
            initialDataSet.current = false
            setReadOnly(false)
            setCreatedOfferID(null)
            setHasFormChanged(false)
            setUploadError("")
            setIsUploading(false)
            setIsSubmitting(false)
            // Snapshot current Redux success state to prevent stale triggers on remount.
            // Without this, navigating back to add after a previous creation causes the
            // success effect to falsely detect the old Redux state as a new creation.
            prevManagementSuccess.current = offers[`${offerType}ManagementSuccess`] || null
        }

        return () => {
            setSelectedMerchant(null)
            setSelectedBrand(null)
            setSelectedFiles([])
            setImageCleared(false)
            initialDataSet.current = false
            setReadOnly(false)
            setCreatedOfferID(null)
            setHasFormChanged(false)
            setUploadError("")
            setIsUploading(false)
            setIsSubmitting(false)
        }
    }, [page, copyFromID, history.location.key])

    useEffect(() => {
        initialDataSet.current = false
    }, [offerID])

    // Track form changes
    const formChanged = useMemo(() => {
        return validation.dirty && Object.keys(validation.touched).length > 0
    }, [validation.dirty, validation.touched])

    useEffect(() => {
        setHasFormChanged(formChanged)
    }, [formChanged])

    // Handle success states
    useEffect(() => {
        const creationSuccess = offers[`${offerType}CreationSuccess`]
        const managementSuccess = offers[`${offerType}ManagementSuccess`]

        // Handle creation success (add mode)
        if (page === "add" && managementSuccess && managementSuccess !== prevManagementSuccess.current) {
            setReadOnly(true)
            setCreatedOfferID(creationSuccess?.offerID || Date.now().toString())
            setIsSubmitting(false)
            prevManagementSuccess.current = managementSuccess

            if (onOfferCreated && creationSuccess?.offerID) {
                onOfferCreated(creationSuccess.offerID)
            }
            if (onTabCompletion) {
                onTabCompletion("1")
            }
        }

        // Handle management/update success (detail mode)
        if (page === "detail" && managementSuccess && managementSuccess !== prevManagementSuccess.current) {
            setIsSubmitting(false)
            setReadOnly(true)
            setHasFormChanged(false)

            // Clear image state if it was removed in the form
            const currentValues = validation.values
            if (currentValues.imageFile === "") {
                setSelectedFiles([])
                setImageCleared(true)
            }

            prevManagementSuccess.current = managementSuccess
        }
    }, [
        offers[`${offerType}CreationSuccess`],
        offers[`${offerType}ManagementSuccess`],
        page,
        offerType,
        onOfferCreated,
        onTabCompletion,
        validation.values
    ])
    

    // Separate effect for error handling
    useEffect(() => {
        if (offerError && Object.keys(offerError).length > 0 && offerError !== prevOfferError.current) {
            setIsSubmitting(false)
            prevOfferError.current = offerError
        }
    }, [offerError])

    // Reset refs when offerID changes
    useEffect(() => {
        prevCreationSuccess.current = null
        // In add mode, snapshot the current Redux success state instead of nulling,
        // so the success effect doesn't falsely trigger from stale Redux state.
        prevManagementSuccess.current = page === "add"
            ? (offers[`${offerType}ManagementSuccess`] || null)
            : null
        prevOfferError.current = null
    }, [offerID])

    // Handle cancel edit
    const handleCancelEdit = useCallback(() => {
        setReadOnly(true)
        setHasFormChanged(false)
        setImageCleared(false) // Reset image cleared flag

        // In add mode with created offer, keep current form values
        // In edit mode, reset to original offer values
        if (page === "add" && createdOfferID) {
            // For add mode after creation, just set readonly without resetting form
            // The form already has the values that were used to create the offer
            return
        }

        // Reset form to original values (for edit mode)
        validation.resetForm({
            values: getInitialValues(offer, offerType, currencies)
        })

        // Reset selected entities
        if (offer?.brand) {
            setSelectedBrand(offer.brand)
        } else {
            setSelectedBrand(null)
        }

        if (offer?.merchant) {
            setSelectedMerchant(offer.merchant)
        } else {
            setSelectedMerchant(null)
        }

        if (offer?.image) {
            const file = { preview: offer.image }
            setSelectedFiles([file])
        } else {
            setSelectedFiles([])
        }
    }, [offer, offerType, currencies, validation, getInitialValues, page, createdOfferID])

    // Column definitions
    const merchantColumns = useMemo(() => [
        {
            Header: "Logo",
            accessor: "logo",
            disableFilters: true,
            filterable: true,
            Cell: cellProps => {
                const merchant = cellProps.row.original
                return (
                    <>
                        {!merchant.logo ? (
                            <div className="avatar-xs">
                                <span className="avatar-title rounded-circle">
                                    {merchant.displayName?.charAt(0) || merchant.legalEntityName?.charAt(0)}
                                </span>
                            </div>
                        ) : (
                            <div>
                                <img
                                    className="rounded-circle avatar-xs"
                                    src={merchant.logo}
                                    alt=""
                                />
                            </div>
                        )}
                    </>
                )
            },
        },
        {
            Header: "Display Name",
            accessor: "displayName",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
        {
            Header: "Legal Entity Name",
            accessor: "legalEntityName",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
        {
            Header: "Legal Entity Address",
            accessor: "legalEntityAddress",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
    ], [])

    const brandColumns = useMemo(() => [
        {
            Header: "Logo",
            accessor: "logo",
            disableFilters: true,
            filterable: true,
            Cell: cellProps => {
                const brand = cellProps.row.original
                return (
                    <>
                        {!brand.logo ? (
                            <div className="avatar-xs">
                                <span className="avatar-title rounded-circle">
                                    {brand.name?.charAt(0)}
                                </span>
                            </div>
                        ) : (
                            <div>
                                <img
                                    className="rounded-circle avatar-xs"
                                    src={brand.logo}
                                    alt=""
                                />
                            </div>
                        )}
                    </>
                )
            },
        },
        {
            Header: "Name",
            accessor: "name",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? cellProps.value : ""
            },
        },
        {
            Header: "Description",
            accessor: "description",
            filterable: true,
            disableFilters: true,
            Cell: cellProps => {
                return cellProps.value ? (
                    <div>
                        {cellProps.value.length > 100
                            ? cellProps.value.substring(0, 100) + "..."
                            : cellProps.value}
                    </div>
                ) : (
                    ""
                )
            },
        }
    ], [])

    const weekDays = [
        { value: "Mon", label: "Monday" },
        { value: "Tue", label: "Tuesday" },
        { value: "Wed", label: "Wednesday" },
        { value: "Thu", label: "Thursday" },
        { value: "Fri", label: "Friday" },
        { value: "Sat", label: "Saturday" },
        { value: "Sun", label: "Sunday" }
    ]

    // Helper function to check if error should be displayed
    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        // Check for common "no data found" patterns
        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }
    return (
        <React.Fragment>
            {canEdit && readOnly && (
                <div className="d-flex justify-content-end mb-3">
                    <AccessControlWrapper
                        actionType={"post"}
                        contextUri={getContextUri(offerType)}
                    >
                        <CButton.Primary
                            text="Edit Offer"
                            onClick={() => {
                                setReadOnly(false)
                                setImageCleared(false) // Reset image cleared flag when entering edit mode
                            }}
                        />
                    </AccessControlWrapper>
                </div>
            )}

            {offerError && shouldDisplayError(offerError) && (
                <Alert color="danger" className="mb-3">
                    {offerError.message || offerError.description || "An error occurred"}
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Form className="needs-validation" onSubmit={validation.handleSubmit}>
                        <Row>


                            {/* Merchant Selection */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>
                                        Merchant
                                        {!readOnly ? "*" : ""}
                                    </Label>
                                    {selectedMerchant ? (
                                        <div className={`border rounded p-3 position-relative ${readOnly ? "input-as-text" : ""}`}>
                                            <div className="d-flex align-items-start">
                                                <div className="avatar-xs me-3">
                                                    {selectedMerchant.logo ? (
                                                        <img
                                                            src={selectedMerchant.logo}
                                                            alt=""
                                                            className="avatar-xs rounded-circle"
                                                        />
                                                    ) : (
                                                        <span className="avatar-title rounded-circle bg-primary">
                                                            {selectedMerchant.displayName?.charAt(0)?.toUpperCase() ||
                                                                selectedMerchant.legalEntityName?.charAt(0)?.toUpperCase() || "M"}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold mb-1">{selectedMerchant.displayName}
                                                        <AccessControlWrapper
                                                            contextUri={url.GET_MERCHANTS}
                                                            actionType="get"
                                                        >
                                                            <IconButton.View
                                                                className="ms-2"
                                                                onClick={() => setShowMerchantDetailModal(true)}
                                                                title="View Merchant Details"
                                                            />
                                                        </AccessControlWrapper>
                                                    </div>
                                                    <div className="text-muted mb-1">
                                                        {selectedMerchant.legalEntityName}
                                                    </div>
                                                    <div className="text-muted">
                                                        {selectedMerchant.briefInfo && selectedMerchant.briefInfo.length > 50
                                                            ? selectedMerchant.briefInfo.substring(0, 50) + "..."
                                                            : selectedMerchant.briefInfo || "---"}
                                                    </div>
                                                </div>
                                                {!readOnly && !(offerType === 'published' && !isFieldEditableForPublished('merchantID')) && (
                                                    <IconButton
                                                        icon="bx bxs-x-circle"
                                                        variant="rounded-circle"
                                                        className="position-absolute right-0 top-0 start-100 translate-middle"
                                                        style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                        onClick={handleMerchantClear}
                                                        title="Clear Merchant"
                                                    />
                                                )}

                                            </div>
                                        </div>
                                    ) : readOnly ? (
                                        <div className="form-control input-as-text text-danger">
                                            Merchant not found
                                        </div>
                                    ) : (
                                        <div>
                                            <CButton.Primary
                                                text="Select Merchant"
                                                icon="bx bx-plus"
                                                onClick={handleMerchantModalOpen}
                                            />
                                        </div>
                                    )}
                                    {validation.touched.merchantID && validation.errors.merchantID ? (
                                        <FormFeedback type="invalid" className="d-block">
                                            {validation.errors.merchantID}
                                        </FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>

                            {/* Brand Selection */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>
                                        Brand
                                        {!readOnly ? "*" : ""}
                                    </Label>
                                    {selectedBrand ? (
                                        <div className={`d-flex border rounded p-2 position-relative ${readOnly ? "input-as-text" : ""}`}>
                                            <div className="avatar-xs me-2">
                                                {selectedBrand.logo ? (
                                                    <img
                                                        src={selectedBrand.logo}
                                                        alt=""
                                                        className="avatar-xs rounded-circle"
                                                    />
                                                ) : (
                                                    <span className="avatar-title rounded-circle bg-primary">
                                                        {selectedBrand.name?.charAt(0)?.toUpperCase() || "B"}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-bold mb-1">{selectedBrand.name}</div>
                                                <div className="text-muted  d-block">
                                                    {selectedBrand.description}
                                                </div>
                                            </div>
                                            {!readOnly && !(offerType === 'published' && !isFieldEditableForPublished('brandID')) && (
                                                <IconButton
                                                    icon="bx bxs-x-circle"
                                                    variant="rounded-circle"
                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                    onClick={handleBrandClear}
                                                    title="Clear Brand"
                                                />
                                            )}
                                        </div>
                                    ) : readOnly ? (
                                        <div className="form-control input-as-text text-danger">
                                            Brand not found
                                        </div>
                                    ) : (
                                        <div>
                                            <CButton.Primary
                                                text="Select Brand"
                                                icon="bx bx-plus"
                                                onClick={handleBrandModalOpen}
                                            />
                                        </div>
                                    )}
                                    {validation.touched.brandID && validation.errors.brandID ? (
                                        <FormFeedback type="invalid" className="d-block">
                                            {validation.errors.brandID}
                                        </FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <TextInput
                                    type="text"
                                    name="label"
                                    placeholder="Enter offer label"
                                    label="Offer Label"
                                    helperText="Max 255 chars allowed"
                                    maxLength={255}
                                    validation={validation}
                                    readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('label'))}
                                    required={true}
                                />
                            </Col>
                            <Col md="6">
                                <TextArea
                                    label="Offer Description"
                                    name="description"
                                    placeholder="Enter offer description"
                                    helperText="Max 510 chars allowed"
                                    maxLength={510}
                                    validation={validation}
                                    readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('description'))}
                                    required={true}
                                    rows={2}
                                />
                            </Col>
                        </Row>

                        {/* Conditional Fields Based on Offer Type */}
                        <Row>
                            <Col md="6">
                                <TextInput
                                    name="userCashbackPercentage"
                                    placeholder="Enter user cashback percentage"
                                    label="User Cashback Percentage"
                                    helperText="Value between 0-100"
                                    validation={validation}
                                    readOnly={readOnly || offerType === 'curator' || (offerType === 'published' && !isFieldEditableForPublished('userCashbackPercentage'))}
                                    required={true}
                                />
                                <TextInput
                                    name="merchantSettlementPercentage"
                                    placeholder="Enter settlement percentage"
                                    label="Merchant Settlement Percentage"
                                    validation={validation}
                                    readOnly={readOnly || offerType === 'operation' || (offerType === 'published' && !isFieldEditableForPublished('merchantSettlementPercentage'))}
                                    required={true}
                                />

                            </Col>
                            <Col md="6">
                                <TextInput
                                    name="maxCashbackAmount"
                                    placeholder="Enter maximum cashback amount"
                                    label="Maximum Cashback Amount"
                                    validation={validation}
                                    readOnly={readOnly || offerType === 'operation' || (offerType === 'published' && !isFieldEditableForPublished('maxCashbackAmount'))}
                                    required={false}
                                />

                                <SelectInput
                                    name="currencyID"
                                    label="Currency"
                                    placeholder="Select currency"
                                    options={currencyOptions}
                                    validation={validation}
                                    readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('currencyID'))}
                                    required={true}
                                />

                            </Col>

                        </Row>




                        <Row>
                            <Col md="6">
                                <TextInput
                                    name="minInvoiceAmount"
                                    placeholder="Enter min invoice amount"
                                    label="Minimum Invoice Amount"
                                    validation={validation}
                                    readOnly={readOnly || offerType === 'operation' || (offerType === 'published' && !isFieldEditableForPublished('minInvoiceAmount'))}
                                    required={false}
                                />
                            </Col>
                            <Col md="6">
                                <TextInput
                                    name="maxInvoiceAmount"
                                    placeholder="Enter max invoice amount"
                                    label="Maximum Invoice Amount"
                                    validation={validation}
                                    readOnly={readOnly || offerType === 'operation' || (offerType === 'published' && !isFieldEditableForPublished('maxInvoiceAmount'))}
                                    required={false}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                {readOnly || (offerType === 'published' && !isFieldEditableForPublished('validityVoph')) ? (
                                    <div className="form-control input-as-text d-flex align-items-center mb-3">
                                        <span>
                                            {validation.values.validityVoph ? "Valid on Public Holidays (VOPH)" : "Not Valid on Public Holidays (VOPH)"}
                                        </span>
                                    </div>
                                ) : (
                                    <CheckboxInput
                                        name="validityVoph"
                                        validation={validation}
                                        checkboxLabel={`Valid on Public Holidays (VOPH)`}
                                        hideInReadonlyWhenEmpty={false}

                                        readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('validityVoph'))}
                                    />
                                )}
                            </Col>
                            {/* Active From/Until fields for all offer types */}
                            <Col md="3">
                                <TextInput
                                    type="datetime-local"
                                    name="activeFrom"
                                    placeholder="Select active from date"
                                    label="Active From"
                                    validation={validation}
                                    readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('activeFrom'))}
                                    required={true}
                                    step="1"
                                />
                            </Col>
                            <Col md="3">
                                <TextInput
                                    type="datetime-local"
                                    name="activeUntil"
                                    placeholder="Select active until date"
                                    label="Active Until"
                                    validation={validation}
                                    readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('activeUntil'))}
                                    required={false}
                                    step="1"
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label>Valid Week Days {readOnly ? "" : "*"}</Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        {weekDays.map(day => (
                                            <CheckboxInput
                                                key={day.value}
                                                name={`weekDay_${day.value}`}
                                                checkboxLabel={day.label}
                                                validation={{
                                                    ...validation,
                                                    setFieldValue: (fieldName, value) => {
                                                        validation.setFieldValue(fieldName, value)
                                                        const currentDays = validation.values.validityWeekdays || []
                                                        const newDays = value
                                                            ? [...currentDays.filter(d => d !== day.value), day.value]
                                                            : currentDays.filter(d => d !== day.value)
                                                        validation.setFieldValue('validityWeekdays', newDays)
                                                    }
                                                }}
                                                readOnly={readOnly || (offerType === 'published' && !isFieldEditableForPublished('validityWeekdays'))}
                                                trueText={day.label}
                                            />
                                        ))}
                                    </div>
                                    {validation.touched.validityWeekdays && validation.errors.validityWeekdays && (
                                        <div className="text-danger mt-2">
                                            {validation.errors.validityWeekdays}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            {/* Offer Image */}
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label>Offer Image{page === "add" && !readOnly ? "" : ""}</Label>

                                    {(selectedFiles.length === 0 || validation.errors.imageFile || isUploading || uploadError) && readOnly === false ? (
                                        <>
                                            {isUploading && (
                                                <div className="text-center p-4">
                                                    <div className="spinner-border text-primary mb-2" />
                                                </div>
                                            )}
                                            {(!isUploading || uploadError) && (
                                                <Dropzone
                                                    multiple={false}
                                                    accept={{
                                                        "image/*": [".png", ".jpeg", ".jpg"],
                                                    }}
                                                    onDrop={handleFileSelection}
                                                    disabled={isUploading}
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <div className="dropzone">
                                                            <div
                                                                className="dz-message needsclick mt-2"
                                                                {...getRootProps()}
                                                            >
                                                                <input {...getInputProps()} />
                                                                <div className="mb-3">
                                                                    <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                                </div>
                                                                <h4>
                                                                    Drop offer image here or click to
                                                                    upload.
                                                                </h4>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Dropzone>
                                            )}
                                        </>
                                    ) : (
                                        readOnly === true && selectedFiles.length === 0 && <p className="fs-5 text-danger">No Image found</p>
                                    )}

                                    {uploadError && (
                                        <FormFeedback type="invalid" className="d-block">
                                            {uploadError}
                                        </FormFeedback>
                                    )}

                                    {selectedFiles.length > 0 && !validation.errors.imageFile ? (
                                        <div className="dropzone-previews mt-3" id="file-previews">
                                            {selectedFiles.map((f, i) => {
                                                return (
                                                    <Card
                                                        className="d-inline-block mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                        key={i + "-file"}
                                                    >
                                                        <div className="p-2">
                                                            {readOnly ? null : (
                                                                <IconButton
                                                                    icon="bx bxs-x-circle"
                                                                    variant="rounded-circle"
                                                                    className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                    style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', color: '#f46a6a' }}
                                                                    onClick={clearImage}
                                                                    title={"Remove Image"}
                                                                />
                                                            )}

                                                            <div>
                                                                <div className="text-center mb-2">
                                                                    <img
                                                                        data-dz-thumbnail=""
                                                                        height="200"
                                                                        className="rounded bg-light"
                                                                        alt={f.name || "Offer Image"}
                                                                        src={f?.preview}
                                                                    />
                                                                </div>
                                                                {f.name && f.formattedSize ? (
                                                                    <div className="text-center">
                                                                        <Link
                                                                            to="#"
                                                                            className="text-muted font-weight-bold"
                                                                        >
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )
                                            })}
                                        </div>
                                    ) : null}

                                    {validation.errors.imageFile ? (
                                        <small
                                            className={`invalid-feedback d-inline-block align-top ${validation.touched.imageFile && validation.errors.imageFile
                                                ? "w-50"
                                                : "w-100"
                                                }`}
                                        >
                                            {validation.errors.imageFile}
                                        </small>
                                    ) : null}

                                    {((selectedFiles.length === 0 && !readOnly) ||
                                        validation.errors.imageFile || uploadError) && (
                                            <span className="text-muted text-end d-inline-block w-100">
                                                JPG & PNG are allowed up to 1 MB max (1100x550)
                                            </span>
                                        )}
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Update Button - Only show for detail/edit mode or add mode after creation */}
                        {!readOnly && (page === "detail" || (page === "add" && createdOfferID)) && (
                            <div className="d-flex gap-2 justify-content-end">
                                <CButton.Secondary
                                    text="Cancel"
                                    onClick={handleCancelEdit}
                                />

                                <ConfirmButton
                                    validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                    onConfirm={handleConfirmedSubmit}
                                    message="Are you sure you want to update this offer? This action will modify the existing offer information."
                                    contextUri={getContextUri(offerType)}
                                    actionType="post"
                                    renderButton={
                                        <CButton.Primary
                                            text="Update Offer"
                                            disabled={!hasFormChanged}
                                        />
                                    }
                                />
                            </div>
                        )}

                        {/* Create Button - Only show for add mode when not readonly and no created offer */}
                        {page === "add" && offerType !== 'operation' && !readOnly && !createdOfferID && (
                            <div className="d-flex gap-2 justify-content-end">
                                <ConfirmButton
                                    validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                    onConfirm={handleConfirmedSubmit}
                                    message="Are you sure you want to create this offer? This action will create a new offer."
                                    contextUri={getContextUri(offerType)}
                                    actionType="post"
                                    renderButton={
                                        <CButton.Primary
                                            text="Create Offer"
                                        />
                                    }
                                />
                            </div>
                        )}
                    </Form>
                </CardBody>
            </Card>

            {/* Merchant Selection Modal */}
            <EntitySelectionModal
                isOpen={showMerchantModal}
                toggle={() => setShowMerchantModal(false)}
                title="Select Merchant"
                data={merchants}
                columns={merchantColumns}
                onItemSelect={handleMerchantSelect}
                selectedItemId={selectedMerchant?.merchantID}
                idField="merchantID"
                onFetchData={handleMerchantFetch}
                searchPlaceholder="Search merchants..."
                emptyMessage="No merchants found"
                refreshContextUri={url.GET_MERCHANTS}
                history={history}
            />

            {/* Brand Selection Modal */}
            <EntitySelectionModal
                isOpen={showBrandModal}
                toggle={() => setShowBrandModal(false)}
                title="Select Brand"
                data={brands}
                columns={brandColumns}
                onItemSelect={handleBrandSelect}
                selectedItemId={selectedBrand?.brandID}
                idField="brandID"
                onFetchData={handleBrandFetch}
                searchPlaceholder="Search brands..."
                emptyMessage="No brands found"
                refreshContextUri={url.GET_BRANDS}
                history={history}
            />

            {/* Merchant Detail Modal */}
            {selectedMerchant && (
                <PopupModal
                    isOpen={showMerchantDetailModal}
                    toggle={() => setShowMerchantDetailModal(false)}
                    title={`Merchant Details - ${selectedMerchant.displayName}`}
                    size="xl"
                    showFooter={false}
                    bodyClassName="p-0"
                >
                    <MerchantDetail
                        match={{ params: { id: selectedMerchant.merchantID } }}
                        history={history}
                    />
                </PopupModal>
            )}
        </React.Fragment>
    )
}